import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { AdjustmentDto, initializedAdjustmentDto } from 'src/app/invoices/dto/adjustment.dto';
import { NotificationService, TranslationService } from 'src/app/shared';
import { AdjustmentTypeService } from '../../shared/adjustment-types/adjustment-type.service';
import { AdjustmentService } from '../../shared/adjustment/adjustment.service';
import { CreditCardRefundRequest } from '../../shared/credit-card/credit-card-refund-request.model';
import { CreditCardRefundResponse } from '../../shared/credit-card/credit-card-refund-response.model';
import { CreditCardService } from '../../shared/credit-card/credit-card.service';
import { initializedUnappliedPaymentDto, UnappliedPaymentDto } from '../../shared/unapplied-payment/dto/unapplied-payment.dto';
import { UnappliedPaymentService } from '../../shared/unapplied-payment/unapplied-payment.service';

@Component({
  selector: 'wl-adjust-unapplied-payment-dialog',
  templateUrl: './adjust-unapplied-payment-dialog.component.html',
  styleUrls: ['./adjust-unapplied-payment-dialog.component.less'],
    standalone: false
})
export class AdjustUnappliedPaymentDialogComponent implements OnInit {
  @Output() adjustmentSaved = new EventEmitter<AdjustmentDto>();

  show = false;
  isSaving = false;
  unappliedPayment = initializedUnappliedPaymentDto();
  adjustment = {} as AdjustmentDto;
  adjustmentTypeItems: SelectItem[] = [];

  get adjustmentSign() {
    if (this._adjustmentIsNegative) {
      return '-';
    } else {
      return '+';
    }
  }

  private _adjustmentIsNegative = true;
  constructor(
    private _adjustmentService: AdjustmentService,
    private _notificationService: NotificationService,
    private _adjustmentTypeService: AdjustmentTypeService,
    private _confirmationService: ConfirmationService,
    private _translationService: TranslationService,
    private _creditCardService: CreditCardService
  ) {}

  ngOnInit(): void {
    this._adjustmentTypeService.getAdjustmentTypes().then((types) => {
      this.adjustmentTypeItems = types.map<SelectItem>((type) => {
        return { label: type.AdjustmentType, value: type.AdjustmentTypeId };
      });
    });
  }

  public open(unappliedPayment: UnappliedPaymentDto) {
    this.show = true;
    this.unappliedPayment = unappliedPayment;
    this.adjustment = initializedAdjustmentDto({
      UnappliedPaymentId: unappliedPayment.UnappliedPaymentId,
      ProfileId: unappliedPayment.ProfileId,
    });
  }

  flipAdjustmentSign() {
    this._adjustmentIsNegative = !this._adjustmentIsNegative;
    this.adjustment.AdjustmentAmount *= -1;
  }

  formatAdjustmentAmount() {
    if (
      (this._adjustmentIsNegative && this.adjustment.AdjustmentAmount > 0) ||
      (!this._adjustmentIsNegative && this.adjustment.AdjustmentAmount < 0)
    ) {
      this.adjustment.AdjustmentAmount *= -1;
    }
  }

  save() {
    this.promptRefundIfCreditPayment();
  }

  close() {
    this.show = false;
  }

  createSimpleAdjustment() {
    this.isSaving = true;
    this._adjustmentService
      .createSimple(this.adjustment)
      .then(() => {
        this.adjustmentSaved.emit(this.adjustment);
        this._notificationService.translateSuccess('common.saved-successfully');
        this.close();
      })
      .finally(() => (this.isSaving = false));
  }

  private promptRefundIfCreditPayment(): void {
    if (this.unappliedPayment.CC_CreditCardNum && this.adjustment.AdjustmentAmount < 0) {
      this._confirmationService.confirm({
        message: this._translationService.getInstant('revenue.transactions-grid.unapplied-payments.confirm-adjustment-refund-message'),
        key: 'refundCreditCardAdjustmentCD',
        accept: () => {
          this.processRefund(this.unappliedPayment);
        },
        reject: () => {
          this._confirmationService.close();
        },
      });
    } else {
      this.createSimpleAdjustment();
    }
  }

  private processRefund(unappliedPayment: UnappliedPaymentDto): Promise<void> {
    this.isSaving = true;
    const refund = new CreditCardRefundRequest();
    refund.CreditCardTransactionId = +unappliedPayment.CC_CreditCardNum.replace('ccid_', '');
    refund.CreateAdjustments = false;
    refund.AdjustmentTypeId = this.adjustment.AdjustmentTypeId;
    refund.RefundAmount = this.adjustment.AdjustmentAmount * -1;
    return this._creditCardService
      .refundCreditCardPayment(refund)
      .then((response) => {
        if (response.Succeeded) {
          this.createSimpleAdjustment();
        } else {
          this._notificationService.error(response.RawTransactionResult);
        }
      })
      .catch((err) => {
        this._notificationService.httpError(err);
        throw err;
      })
      .finally(() => (this.isSaving = false));
  }
}
